"use client";

import { Feld, FeldHuelle, grundGroesse, type FeldEigenschaften, type FeldGroesse, type FeldGroesseGrund } from "./feld";

/**
 * Einzeilige Eingaben (Musterkatalog M-002): `Textfeld`, `Datumsfeld` (F9) und `Suchfeld`.
 * Klassenketten sind fest und vollständig (nie zusammengesetzt, Plan Regel 8): Größe × Fehlerzustand.
 * Bei Feldfehler (F3) färbt sich Rahmen und Fokusring rot.
 */
type EingabeRest = Omit<React.InputHTMLAttributes<HTMLInputElement>, "id" | "className" | "style" | "type" | "size" | "children">;

const FELD: Record<FeldGroesse, { normal: string; fehler: string }> = {
  standard: {
    normal:
      "py-2.5 sm:py-3 px-4 rounded-lg block w-full bg-layer border-layer-line sm:text-sm text-foreground placeholder:text-muted-foreground-1 focus:border-primary-focus focus:ring-primary-focus disabled:opacity-50 disabled:pointer-events-none",
    fehler:
      "py-2.5 sm:py-3 ps-4 pe-10 rounded-lg block w-full bg-layer border-red-500 sm:text-sm text-foreground placeholder:text-muted-foreground-1 focus:border-red-500 focus:ring-red-500 disabled:opacity-50 disabled:pointer-events-none",
  },
  kompakt: {
    normal:
      "py-1.5 sm:py-2 px-3 rounded-lg block w-full bg-layer border-layer-line sm:text-sm text-foreground placeholder:text-muted-foreground-1 focus:border-primary-focus focus:ring-primary-focus disabled:opacity-50 disabled:pointer-events-none",
    fehler:
      "py-1.5 sm:py-2 ps-3 pe-10 rounded-lg block w-full bg-layer border-red-500 sm:text-sm text-foreground placeholder:text-muted-foreground-1 focus:border-red-500 focus:ring-red-500 disabled:opacity-50 disabled:pointer-events-none",
  },
  // ruhig (A4′): Rahmen und Grund nur bei Hover und Fokus; der Fokusrahmen (Rand + Ring) ist immer sichtbar.
  ruhig: {
    normal:
      "py-1.5 sm:py-2 px-2 rounded-md block w-full bg-transparent border-transparent sm:text-sm text-foreground placeholder:text-muted-foreground-1 hover:border-layer-line hover:bg-layer focus:bg-layer focus:border-primary-focus focus:ring-primary-focus disabled:opacity-50 disabled:pointer-events-none",
    fehler:
      "py-1.5 sm:py-2 ps-2 pe-10 rounded-md block w-full bg-layer border-red-500 sm:text-sm text-foreground placeholder:text-muted-foreground-1 focus:border-red-500 focus:ring-red-500 disabled:opacity-50 disabled:pointer-events-none",
  },
};

/** Suchfeld: Symbol links; kompakt = gedämpfter Grund wie die Filterleiste der Kanban-Vorlage. */
const SUCHE: Record<FeldGroesseGrund, string> = {
  standard:
    "py-2.5 sm:py-3 ps-10 pe-4 rounded-lg block w-full bg-layer border-layer-line sm:text-sm text-foreground placeholder:text-muted-foreground-1 focus:border-primary-focus focus:ring-primary-focus disabled:opacity-50 disabled:pointer-events-none",
  kompakt:
    "py-1 sm:py-1.5 px-8 block w-full bg-surface border-transparent text-surface-foreground placeholder:text-muted-foreground-1 rounded-lg sm:text-sm focus:bg-layer focus:border-primary-focus focus:ring-primary-focus disabled:opacity-50 disabled:pointer-events-none",
};
const SUCH_SYMBOL_STANDARD = "absolute inset-y-0 inset-s-0 flex items-center pointer-events-none z-20 ps-3.5";
const SUCH_SYMBOL_KOMPAKT = "absolute inset-y-0 inset-s-0 flex items-center pointer-events-none z-20 ps-2.5";

export type TextfeldArt = "text" | "email" | "number" | "tel" | "url";

export function Textfeld({
  art = "text",
  ...alle
}: FeldEigenschaften & EingabeRest & { art?: TextfeldArt }) {
  return <Einzeiler eingabeArt={art} {...alle} />;
}

/** `Datumsfeld.art` → natives `type` (F9). `zeit` = reine Uhrzeit (Betreiber 12.10.2026, K2): Wert immer `HH:MM` (24 h), die Anzeige bestimmt das Gerät. */
const DATUMSFELD_TYP = { datum: "date", "datum-zeit": "datetime-local", zeit: "time" } as const;

/**
 * F9: natives Datum, Datum + Uhrzeit bzw. reine Uhrzeit (`art="zeit"`, Beginn einer Besprechung); eine Kalenderkarte nur auf
 * ausdrücklichen Wunsch. Der Wert von `type="time"` ist unabhängig von der Anzeige (12 h/24 h) immer `HH:MM` bzw. leer.
 */
export function Datumsfeld({
  art = "datum",
  ...alle
}: FeldEigenschaften & EingabeRest & { art?: keyof typeof DATUMSFELD_TYP }) {
  return <Einzeiler eingabeArt={DATUMSFELD_TYP[art]} oeffnetPicker {...alle} />;
}

/**
 * F9 (Betreiber W129 a, 13.10.2026): Klick ins Datums-/Zeitfeld öffnet den nativen Picker. Nur `onClick` (nicht `onFocus`:
 * der Tab-Durchlauf bleibt ruhig), nicht bei `disabled`/`readOnly`; Tippen bleibt möglich. `showPicker` fehlt in älteren
 * Browsern oder wirft (z. B. ohne Nutzergeste): dann bleibt es beim nativen Verhalten.
 */
export function oeffneNativenPicker(eingabe: HTMLInputElement): void {
  if (eingabe.disabled || eingabe.readOnly) return;
  try {
    eingabe.showPicker?.();
  } catch {
    // Kein Picker verfügbar: Eingabe per Tastatur bleibt.
  }
}

function Einzeiler({
  eingabeArt,
  oeffnetPicker = false,
  id,
  label,
  labelVersteckt,
  optional,
  hilfe,
  fehler,
  hinweissatz,
  feldhilfe,
  layout,
  groesse = "standard",
  ...rest
}: FeldEigenschaften & EingabeRest & { eingabeArt: string; oeffnetPicker?: boolean }) {
  return (
    <Feld id={id} label={label} labelVersteckt={labelVersteckt} optional={optional} hilfe={hilfe} fehler={fehler} hinweissatz={hinweissatz} feldhilfe={feldhilfe} layout={layout} groesse={groesse}>
      {(a) => (
        <FeldHuelle>
          {/* Preline: component/input/input-corner-hint */}
          <input
            {...rest}
            id={a.id}
            type={eingabeArt}
            onClick={(e) => {
              rest.onClick?.(e);
              if (oeffnetPicker) oeffneNativenPicker(e.currentTarget);
            }}
            className={a.fehlerhaft ? FELD[groesse].fehler : FELD[groesse].normal}
            aria-describedby={a["aria-describedby"]}
            aria-invalid={a["aria-invalid"]}
          />
        </FeldHuelle>
      )}
    </Feld>
  );
}

/** Suchfeld (Listenkopf, Filter): Label nur für Hilfstechnik, Symbol links. */
export function Suchfeld({
  id,
  label,
  labelVersteckt = true,
  optional,
  hilfe,
  fehler,
  hinweissatz,
  feldhilfe,
  layout,
  groesse = "kompakt",
  ...rest
}: FeldEigenschaften & EingabeRest) {
  return (
    <Feld id={id} label={label} labelVersteckt={labelVersteckt} optional={optional} hilfe={hilfe} fehler={fehler} hinweissatz={hinweissatz} feldhilfe={feldhilfe} layout={layout} groesse={groesse}>
      {(a) => (
        <div className="relative">
          {/* Preline: component/input/input-label */}
          <div className={groesse === "kompakt" ? SUCH_SYMBOL_KOMPAKT : SUCH_SYMBOL_STANDARD}>
            <svg
              className="shrink-0 size-3.5 text-muted-foreground-1"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </div>
          <input
            {...rest}
            id={a.id}
            type="search"
            className={SUCHE[grundGroesse(groesse)]}
            aria-describedby={a["aria-describedby"]}
            aria-invalid={a["aria-invalid"]}
          />
        </div>
      )}
    </Feld>
  );
}
